import { withStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { CAMPAIGN_INTERNAL_STATUSES } from 'shared/constants'
import useGraphQLFetch from 'Utils/Hooks/useGraphQLFetch'
import { getClientCodesInfo } from '../../gql/getClientCodesInfo'
import CodeExplanation from '../CodeExplanation'
import ClientCodesTable from '../CodesTable/ClientCodesTable/ClientCodesTable'
import ClientCodesButtons from '../CommonComponents/ClientCodesButtons'
import Loader from '../CommonComponents/Loader'
import PageWrapper from '../CommonComponents/PageWrapper'
import RequiredDataTable from '../RequiredDataTable/RequiredDataTable'
import styles from './styles'

const ClientView = ({ classes, campaigns }) => {
  const { campaignId } = useParams()
  const campaign = campaigns[campaignId]
  const [internalStatus, setInternalStatus] = useState(
    campaign.selfServeData[0].internalStatus,
  )
  const isActionsDisabled =
    internalStatus !== CAMPAIGN_INTERNAL_STATUSES.UPLOAD_CODES

  const isCodesUploadedAlready =
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.PENDING_DIGITAL_PROOF ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.PAYMENT ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.ACTIVE ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.COMPLETED

  const [codesTable, setCodesTable] = useState(null)
  const [showCodesExplanationPage, setShowCodesExplanationPage] = useState(
    false,
  )
  const [isCodesUploading, setIsCodesUploading] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [currentTrackingType, setCurrentTrackingType] = useState('')
  const [isDuplicateCodesExist, setIsDuplicateCodesExist] = useState(false)
  const [isDuplicateLinksExist, setIsDuplicateLinksExist] = useState(false)
  const [countCodesLinks, setCountCodesLinks] = useState({
    codes: {},
    links: {},
  })

  const tableInfo = useMemo(() => {
    if (currentTrackingType) {
      return campaign.cityPlans.nodes
        .map(node => {
          if (node.missions.totalCount) {
            return {
              city: node.city.name,
              codesQuantity: codesTable.reduce((sum, code) => {
                if (code.city.id === node.city.id && code.clientCode) {
                  return sum + 1
                }
                return sum
              }, 0),
              linksQuantity: codesTable.reduce((sum, code) => {
                if (code.city.id === node.city.id && code.clientLink) {
                  return sum + 1
                }
                return sum
              }, 0),
              requiredQuantity:
                currentTrackingType === 'BASIC' ? 1 : node.missions.totalCount,
            }
          }
          return null
        })
        .filter(city => city)
    }
    return []
  }, [campaign, currentTrackingType, codesTable])

  const onCodesDeleted = () => {
    setSelectedRows([])
  }

  const mapCodesTable = (codesTable, trackingType = currentTrackingType) => {
    let isDuplicateCodesExist = false
    let isDuplicateLinksExist = false
    const countCodesLinks = {
      codes: {},
      links: {},
    }
    setCodesTable(codesTable)
    setCurrentTrackingType(trackingType)
    codesTable.forEach(row => {
      const code = row.clientCode
      const link = row.clientLink
      if (countCodesLinks.codes[code]) {
        countCodesLinks.codes[code] += 1
        isDuplicateCodesExist = true
      } else {
        countCodesLinks.codes[code] = 1
      }
      if (countCodesLinks.links[link]) {
        countCodesLinks.links[link] += 1
        isDuplicateLinksExist = true
      } else {
        countCodesLinks.links[link] = 1
      }
    })
    setIsDuplicateCodesExist(isDuplicateCodesExist)
    setIsDuplicateLinksExist(isDuplicateLinksExist)
    setCountCodesLinks(countCodesLinks)
  }

  const { isLoading } = useGraphQLFetch({
    fetchFn: getClientCodesInfo,
    defaultFetchArgs: {
      campaignId: campaignId,
    },
    mapResponse: data => {
      const codesTable = data.campaignClientCodes.clientCodes
      const trackingType = data.campaignClientCodes.trackingType
      mapCodesTable(codesTable, trackingType)
    },
    skipFetchOnInit: false,
  })

  if (showCodesExplanationPage) {
    return (
      <CodeExplanation onClose={() => setShowCodesExplanationPage(false)} />
    )
  }

  return (
    <div>
      {isLoading ? <Loader /> : null}
      <div className={classes.headerPart}>
        <div className={classes.title}>Codes</div>
        <div
          className={classes.helper}
          onClick={() => {
            setShowCodesExplanationPage(true)
          }}
        >
          <InfoOutlinedIcon className={classes.icon} fontSize="small" />
          What are codes and how do they work?
        </div>
      </div>
      <PageWrapper>
        <div className={classes.codes}>
          <div className={classes.block}>
            <ClientCodesButtons
              mapCodesTable={mapCodesTable}
              isCodesUploading={isCodesUploading}
              setIsCodesUploading={setIsCodesUploading}
              selectedRows={selectedRows}
              onCodesDeleted={onCodesDeleted}
              isActionsDisabled={isActionsDisabled}
              setInternalStatus={setInternalStatus}
              setShowCodesExplanationPage={setShowCodesExplanationPage}
            />
            {codesTable && codesTable.length ? (
              <div className={classes.table}>
                <ClientCodesTable
                  codes={codesTable}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  countCodesLinks={countCodesLinks}
                  disabled={isActionsDisabled}
                />
              </div>
            ) : null}
          </div>
          <div className={classes.block}>
            <RequiredDataTable
              disabled={isActionsDisabled}
              tableInfo={tableInfo}
              title={'Required Data'}
              isDuplicateCodesExist={isDuplicateCodesExist}
              isDuplicateLinksExist={isDuplicateLinksExist}
              isCodesUploadedAlready={isCodesUploadedAlready}
              setInternalStatus={setInternalStatus}
              currentTrackingType={currentTrackingType}
            />
          </div>
        </div>
      </PageWrapper>
    </div>
  )
}

const mapStateToProps = (state, props) => ({
  campaigns: state.campaigns.campaigns,
  state: state,
})

export default connect(mapStateToProps)(withStyles(styles)(ClientView))
